import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PrintReceiptDialog from '../components/PrintReceiptDialog';
import { useAutomaticPrintingPlacement } from '../hooks/useiOSPlacementId';
import usePrintReceipt from '../hooks/usePrintReceipt';
import useSoftpayReceipt from '../hooks/useSoftpayReceipt';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useSoftpayAppId } from './SoftpayAppIdStore';
import { usePrintSoftpayReceipt } from '@ordr-as/ordr-service-types/epos';
import { isF1Device } from '../utils/isF1Device';

export type PrintReceiptContextType = {
  bookingReferenceToPrint: string | undefined;
  setBookingReferenceToPrint: (reference: string) => void;
  handlePrintReceipt: (reference: string, paymentMethodId?: string) => void;
  isBatteryTooLow: boolean | null;
  showBatteryWarningDialog: boolean;
};

export const PrintReceiptContext = createContext<PrintReceiptContextType | undefined>(undefined);

const getBookingReferenceFromPaymentReference = (paymentReference: string) => {
  const split = paymentReference.split('-');
  return split.length > 2 ? paymentReference : split[0];
};

interface ProviderProps {
  children?: React.ReactNode;
}

export const PrintReceiptContextProvider = ({ children }: ProviderProps) => {
  const [bookingReferenceToPrint, setBookingReferenceToPrint] = useState<string>();
  const [isBatteryTooLow, setIsBatteryTooLow] = useState<boolean | null>(null);
  const [showBatteryWarningDialog, setShowBatteryWarningDialog] = useState<boolean>(false);
  const { defaultPlacement } = useAutomaticPrintingPlacement();
  const { printReceipt } = usePrintReceipt();
  const printSoftpayReceipt = useSoftpayReceipt();
  const softpayAppId = useSoftpayAppId();
  const isF1 = isF1Device();
  const { mutate: printHubReceipt } = usePrintSoftpayReceipt();

  const handlePrintReceipt = useCallback(
    async (paymentReference: string, paymentMethodId?: string) => {
      let batteryTooLow = false;
      // @ts-ignore
      if (navigator.getBattery) {
        // @ts-ignore
        const battery = await navigator.getBattery();

        batteryTooLow = battery.level < 0.1;
        setIsBatteryTooLow(batteryTooLow);
      }

      if (batteryTooLow) {
        setShowBatteryWarningDialog(true);
        return;
      }

      if (isF1 && paymentMethodId) {
        printHubReceipt({
          data: {
            paymentReference: paymentReference,
            paymentMethodId: paymentMethodId,
            traceId: `${paymentReference}-${Math.floor(Date.now() / 1000)}`,
          },
        });
        return;
      }

      if (softpayAppId) {
        printSoftpayReceipt(paymentReference);
        return;
      }

      const bookingReference = getBookingReferenceFromPaymentReference(paymentReference);

      if (defaultPlacement && bookingReference) {
        printReceipt(bookingReference, defaultPlacement);
        return;
      }

      setBookingReferenceToPrint(bookingReference);
    },
    [defaultPlacement, isF1, printHubReceipt, printReceipt, printSoftpayReceipt, softpayAppId]
  );

  const contextValue = useMemo(() => {
    const newValue = {
      bookingReferenceToPrint,
      setBookingReferenceToPrint,
      isBatteryTooLow,
      showBatteryWarningDialog,
      handlePrintReceipt,
    };

    return newValue;
  }, [bookingReferenceToPrint, handlePrintReceipt, isBatteryTooLow, showBatteryWarningDialog]);

  return (
    <PrintReceiptContext.Provider value={contextValue}>
      {showBatteryWarningDialog && (
        <Dialog
          title="Could not print receipt"
          onClose={() => setShowBatteryWarningDialog(false)}
          actionLabel="OK"
          onAction={() => setShowBatteryWarningDialog(false)}
        >
          <div>
            <p>The device must have at least 10% battery to print. Charge the device and try again.</p>
          </div>
        </Dialog>
      )}
      {bookingReferenceToPrint && (
        <PrintReceiptDialog
          onCloseDialog={() => setBookingReferenceToPrint(undefined)}
          reference={bookingReferenceToPrint}
        />
      )}
      {children}
    </PrintReceiptContext.Provider>
  );
};

export const usePrintReceiptContext = (): PrintReceiptContextType => {
  const context = useContext(PrintReceiptContext);
  if (!context) {
    throw new Error('usePrintReceiptContext must be used within a PrintReceiptProvider');
  }
  return context;
};
